package sarka.design.skoria.messaging

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * Wire shapes for direct messaging. These mirror the server's
 * `sarka.design.messaging` types and must stay in sync with them.
 *
 * [payload] is opaque on the wire and in the database. Only the client decodes
 * it, via [MessageCodec]. Don't add logic here that inspects it.
 */
@Serializable
data class DirectMessage(
    val id: String,
    val sender: String,
    val recipient: String,
    val payload: String,
    val payloadVersion: Int,
    val sentAt: Long,
    val deliveredAt: Long? = null,
    val readAt: Long? = null,
)

@Serializable
data class ConversationSummary(
    val username: String,
    val lastPayload: String,
    val lastPayloadVersion: Int,
    val lastMessageAt: Long,
    val lastMessageFromMe: Boolean,
    val unreadCount: Int,
)

@Serializable
data class SendMessageRequest(
    val recipient: String,
    val payload: String,
    val payloadVersion: Int = 0,
)

@Serializable
data class SendMessageResponse(
    val message: DirectMessage,
    val deliveredLive: Boolean,
)

@Serializable
data class ConversationResponse(
    val withUser: String,
    val messages: List<DirectMessage>,
    val hasMore: Boolean,
)

@Serializable
data class MarkReadRequest(
    val withUser: String,
)

/** Frames on the /dm socket. Mirrors the server's sealed DmFrame. */
@Serializable
sealed class DmFrame {

    @Serializable
    @SerialName("message")
    data class Incoming(val message: DirectMessage) : DmFrame()

    @Serializable
    @SerialName("ack")
    data class Ack(val messageId: String) : DmFrame()

    @Serializable
    @SerialName("ready")
    data class Ready(val username: String) : DmFrame()

    @Serializable
    @SerialName("error")
    data class Error(val message: String) : DmFrame()

    /**
     * Server -> client: messages we sent to [withUser] have been delivered or
     * read. Lets the conversation update its Sent/Delivered/Read labels live.
     */
    @Serializable
    @SerialName("receipt")
    data class Receipt(
        val withUser: String,
        /**
         * "delivered" or "read". Named [state], not "type": "type" is the
         * polymorphic discriminator for DmFrame and a field of that name breaks
         * serialization. Must match the server field name.
         */
        val state: String,
        val at: Long,
    ) : DmFrame()
}

/** Receipt type constants, matching the server. */
object ReceiptType {
    const val DELIVERED = "delivered"
    const val READ = "read"
}
